/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {IndexedObject} from "@dvrd/dvr-controls";
import {AccessKey, ContextShape, UserRight, UserRightItem, UserRightType, UserRole} from "./interfaces";
import Account from "../models/accountModel";
import {hasAdminLogin} from "./adminUtil";

export function userHasRight(accountOrContext: Account | ContextShape | null, accessKey: AccessKey | Array<AccessKey>,
                             accountID?: string): boolean {
    if (hasAdminLogin()) return true;
    const account: Account | null = determineAccount(accountOrContext, accountID);
    if (!account) return false;
    if (account.isAccountOwner()) return true;
    const rights = account.userRights;
    const accessKeys = Array.isArray(accessKey) ? accessKey : [accessKey];
    for (const right of rights) {
        if (!accessKeys.includes(right.key)) continue;
        if (right.allow) return true;
    }
    return false;
}

function determineAccount(accountOrContext: Account | ContextShape | null,
                          accountID: string | undefined): Account | null {
    if (!accountOrContext) return null;
    if (accountOrContext instanceof Account) return accountOrContext;
    if (!accountID) return accountOrContext.accountContext.account;
    return accountOrContext.accountContext.accounts.find((account: Account) => account.id === accountID) ?? null;
}

export function translateUserRole(role: UserRole | null): string {
    switch (role) {
        case UserRole.ADMIN:
            return 'Administrator';
        case UserRole.EMPLOYEE:
            return 'Medewerker';
        case UserRole.OWNER:
            return 'Eigenaar';
        case UserRole.ACCOUNTANT:
            return 'Boekhouder';
        case UserRole.CUSTOMER_SERVICE:
            return 'Klantenservice';
        case null:
            return '';
    }
}

export function getDefaultRoleRights(role: UserRole): Array<UserRight> {
    const rights = DEFAULT_ROLE_RIGHTS[role];
    if (!rights) return [];
    return rights.map((key: AccessKey) => ({key: key, right_type: UserRightType.ACCESS, allow: true}));
}

export function getDefaultRoute(rights: Array<UserRight> | undefined, userRole: UserRole | undefined) {
    if (userRole === UserRole.OWNER) return '/facturen/orders';
    if (findRight(rights, AccessKey.PAGE_INVOICES)?.allow) return '/facturen/orders';
    else if (findRight(rights, AccessKey.PAGE_INVOICE_REQUESTS)?.allow) return '/facturen/aanvragen';
    else if (findRight(rights, AccessKey.PAGE_RETURNS)?.allow) return '/retouren';
    else if (findRight(rights, AccessKey.PAGE_EMAIL)?.allow) return '/emails';
    else if (findRight(rights, AccessKey.PAGE_SALES_DASHBOARD)?.allow) return '/sales-dashboard';
    else if (findRight(rights, AccessKey.PAGE_REVIEWS_PRODUCTS)?.allow) return '/review-analytics';
    else if (findRight(rights, AccessKey.PAGE_VAT_DECLARATION)?.allow) return '/btw-aangifte';
    else if (findRight(rights, AccessKey.PAGE_SEARCH_TERMS)?.allow) return '/zoek-trends';
    return '/instellingen/algemeen';
}

function findRight(rights: Array<UserRight> | undefined,
                   accessKey: AccessKey | Array<AccessKey>): UserRight | undefined {
    if (!rights) return undefined;
    if (!Array.isArray(accessKey)) accessKey = [accessKey];
    return rights.find((right: UserRight) => accessKey.includes(right.key));
}

export const DEFAULT_ROLE_RIGHTS: { [key in UserRole]: Array<AccessKey> } = {
    [UserRole.OWNER]: [],
    [UserRole.ACCOUNTANT]: [
        AccessKey.PAGE_INVOICES,
        AccessKey.PAGE_ORDERS,
        AccessKey.PAGE_RETURNS,
        AccessKey.PAGE_SALES_DASHBOARD,
        AccessKey.PAGE_VAT_DECLARATION,
        AccessKey.PAGE_SETTINGS_PRODUCTS,
        AccessKey.PAGE_SETTINGS_EXPORT,
        AccessKey.PAGE_SETTINGS_SUBSCRIPTION,
        AccessKey.INVOICE_DETAILS,
        AccessKey.INVOICE_DOWNLOAD,
        AccessKey.INVOICE_BULK_DOWNLOAD,
        AccessKey.INVOICE_STATS,
        AccessKey.INVOICE_CREDIT,
        AccessKey.INVOICE_REVERSE_CHARGE,
        AccessKey.RETURN_DETAILS,
        AccessKey.SALES_DASHBOARD_EXPORT,
        AccessKey.SALES_DASHBOARD_COST_PRICES,
        AccessKey.VAT_DECLARATION_EXPORT,
        AccessKey.VAT_DECLARATION_DOWNLOAD_SPEC,
        AccessKey.PRODUCT_SETTINGS_EDIT_VAT,
        AccessKey.PRODUCT_SETTINGS_UPLOAD,
        AccessKey.EXPORT_SETTINGS_DOWNLOAD,
        AccessKey.EXPORT_SETTINGS_EXPORT
    ],
    [UserRole.CUSTOMER_SERVICE]: [
        AccessKey.PAGE_INVOICES,
        AccessKey.PAGE_ORDERS,
        AccessKey.PAGE_INVOICE_REQUESTS,
        AccessKey.PAGE_RETURNS,
        AccessKey.INVOICE_DETAILS,
        AccessKey.INVOICE_EDIT_CUSTOMER,
        AccessKey.INVOICE_EDIT_ITEMS,
        AccessKey.INVOICE_DOWNLOAD,
        AccessKey.INVOICE_SEND,
        AccessKey.INVOICE_CREDIT,
        AccessKey.INVOICE_REVERSE_CHARGE,
        AccessKey.INVOICE_INVOICE_NUMBER,
        AccessKey.INVOICE_REQUEST_UPLOAD,
        AccessKey.INVOICE_REQUEST_ARCHIVE,
        AccessKey.RETURN_DETAILS,
        AccessKey.RETURN_CREATE_CREDIT,
        AccessKey.RETURN_SEND_CREDIT,
        AccessKey.RETURN_BOL,
    ],
    [UserRole.EMPLOYEE]: [
        AccessKey.PAGE_INVOICES,
        AccessKey.PAGE_ORDERS,
        AccessKey.PAGE_INVOICE_REQUESTS,
        AccessKey.PAGE_RETURNS,
        AccessKey.PAGE_INVOICE_LAYOUT,
        AccessKey.PAGE_EMAIL,
        AccessKey.PAGE_NOTIFICATIONS,
        AccessKey.PAGE_SALES_DASHBOARD,
        AccessKey.PAGE_REVIEWS_PRODUCTS,
        AccessKey.PAGE_REVIEWS_FEED,
        AccessKey.PAGE_REVIEWS_SETTINGS,
        AccessKey.PAGE_REVIEWS_EXPORT,
        AccessKey.PAGE_VAT_DECLARATION,
        AccessKey.PAGE_SEARCH_TERMS,
        AccessKey.PAGE_SETTINGS_API,
        AccessKey.PAGE_SETTINGS_PRODUCTS,
        AccessKey.PAGE_SETTINGS_INVOICE,
        AccessKey.PAGE_SETTINGS_EMAILS,
        AccessKey.PAGE_SETTINGS_EXPORT,
        AccessKey.INVOICE_DETAILS,
        AccessKey.INVOICE_STATS,
        AccessKey.INVOICE_BULK_DOWNLOAD,
        AccessKey.INVOICE_EDIT_CUSTOMER,
        AccessKey.INVOICE_EDIT_ITEMS,
        AccessKey.INVOICE_DOWNLOAD,
        AccessKey.INVOICE_SEND,
        AccessKey.INVOICE_CREDIT,
        AccessKey.INVOICE_REVERSE_CHARGE,
        AccessKey.INVOICE_CANCEL_EMAILS,
        AccessKey.INVOICE_INVOICE_NUMBER,
        AccessKey.INVOICE_REQUEST_UPLOAD,
        AccessKey.INVOICE_REQUEST_SETTINGS,
        AccessKey.INVOICE_REQUEST_ARCHIVE,
        AccessKey.INVOICE_REQUEST_STATS,
        AccessKey.RETURN_DETAILS,
        AccessKey.RETURN_BOL,
        AccessKey.RETURN_SEND_CREDIT,
        AccessKey.RETURN_CREATE_CREDIT,
        AccessKey.EMAIL_EDIT,
        AccessKey.EMAIL_STATS,
        AccessKey.EMAIL_EDIT_AUTOMATIC,
        AccessKey.EMAIL_TEST_MAIl,
        AccessKey.EMAIL_COPY,
        AccessKey.NOTIFICATIONS_EDIT,
        AccessKey.NOTIFICATIONS_DELETE,
        AccessKey.SALES_DASHBOARD_COST_PRICES,
        AccessKey.SALES_DASHBOARD_EXPORT,
        AccessKey.RATING_VIEW,
        AccessKey.RATING_SETTINGS,
        AccessKey.RATING_CREATE_EXPORT,
        AccessKey.RATING_DOWNLOAD_EXPORT,
        AccessKey.VAT_DECLARATION_EXPORT,
        AccessKey.VAT_DECLARATION_DOWNLOAD_SPEC,
        AccessKey.SETTING_EDIT_COMPANY,
        AccessKey.API_SETTINGS_DISCONNECT,
        AccessKey.API_SETTINGS_SETUP,
        AccessKey.PRODUCT_SETTINGS_EDIT_TITLE,
        AccessKey.PRODUCT_SETTINGS_EDIT_VAT,
        AccessKey.PRODUCT_SETTINGS_UPLOAD,
        AccessKey.PRODUCT_SETTINGS_SYNC,
        AccessKey.INVOICE_SETTINGS_EDIT,
        AccessKey.EMAIL_SETTINGS_EDIT,
        AccessKey.EXPORT_SETTINGS_EXPORT,
        AccessKey.EXPORT_SETTINGS_DOWNLOAD,
    ],
    [UserRole.ADMIN]: [
        AccessKey.PAGE_INVOICES,
        AccessKey.PAGE_ORDERS,
        AccessKey.PAGE_INVOICE_REQUESTS,
        AccessKey.PAGE_RETURNS,
        AccessKey.PAGE_INVOICE_LAYOUT,
        AccessKey.PAGE_EMAIL,
        AccessKey.PAGE_NOTIFICATIONS,
        AccessKey.PAGE_SALES_DASHBOARD,
        AccessKey.PAGE_REVIEWS_PRODUCTS,
        AccessKey.PAGE_REVIEWS_FEED,
        AccessKey.PAGE_REVIEWS_SETTINGS,
        AccessKey.PAGE_REVIEWS_EXPORT,
        AccessKey.PAGE_VAT_DECLARATION,
        AccessKey.PAGE_SEARCH_TERMS,
        AccessKey.PAGE_ACCOUNT_USERS,
        AccessKey.PAGE_SETTINGS_API,
        AccessKey.PAGE_SETTINGS_PRODUCTS,
        AccessKey.PAGE_SETTINGS_INVOICE,
        AccessKey.PAGE_SETTINGS_EMAILS,
        AccessKey.PAGE_SETTINGS_EXPORT,
        AccessKey.INVOICE_DETAILS,
        AccessKey.INVOICE_STATS,
        AccessKey.INVOICE_BULK_DOWNLOAD,
        AccessKey.INVOICE_EDIT_CUSTOMER,
        AccessKey.INVOICE_EDIT_ITEMS,
        AccessKey.INVOICE_DOWNLOAD,
        AccessKey.INVOICE_SEND,
        AccessKey.INVOICE_CREDIT,
        AccessKey.INVOICE_REVERSE_CHARGE,
        AccessKey.INVOICE_CANCEL_EMAILS,
        AccessKey.INVOICE_INVOICE_NUMBER,
        AccessKey.INVOICE_REQUEST_UPLOAD,
        AccessKey.INVOICE_REQUEST_SETTINGS,
        AccessKey.INVOICE_REQUEST_ARCHIVE,
        AccessKey.INVOICE_REQUEST_STATS,
        AccessKey.RETURN_DETAILS,
        AccessKey.RETURN_BOL,
        AccessKey.RETURN_SEND_CREDIT,
        AccessKey.RETURN_CREATE_CREDIT,
        AccessKey.EMAIL_EDIT,
        AccessKey.EMAIL_STATS,
        AccessKey.EMAIL_EDIT_AUTOMATIC,
        AccessKey.EMAIL_TEST_MAIl,
        AccessKey.EMAIL_COPY,
        AccessKey.NOTIFICATIONS_EDIT,
        AccessKey.NOTIFICATIONS_DELETE,
        AccessKey.SALES_DASHBOARD_COST_PRICES,
        AccessKey.SALES_DASHBOARD_EXPORT,
        AccessKey.RATING_VIEW,
        AccessKey.RATING_SETTINGS,
        AccessKey.RATING_CREATE_EXPORT,
        AccessKey.RATING_DOWNLOAD_EXPORT,
        AccessKey.VAT_DECLARATION_EXPORT,
        AccessKey.VAT_DECLARATION_DOWNLOAD_SPEC,
        AccessKey.SETTING_EDIT_COMPANY,
        AccessKey.SETTINGS_DELETE_COMPANY,
        AccessKey.API_SETTINGS_DISCONNECT,
        AccessKey.API_SETTINGS_SETUP,
        AccessKey.PRODUCT_SETTINGS_EDIT_TITLE,
        AccessKey.PRODUCT_SETTINGS_EDIT_VAT,
        AccessKey.PRODUCT_SETTINGS_UPLOAD,
        AccessKey.PRODUCT_SETTINGS_SYNC,
        AccessKey.INVOICE_SETTINGS_EDIT,
        AccessKey.EMAIL_SETTINGS_EDIT,
        AccessKey.EXPORT_SETTINGS_EXPORT,
        AccessKey.EXPORT_SETTINGS_DOWNLOAD,
        AccessKey.ACCOUNT_USERS_INVITE,
        AccessKey.ACCOUNT_USERS_EDIT,
    ],
}

export const AVAILABLE_USER_RIGHTS: IndexedObject<Array<UserRightItem>> = {
    'Toegang tot pagina\'s': [
        {
            label: 'Facturen', key: AccessKey.PAGE_INVOICES, right_type: UserRightType.ACCESS, children: [
                {label: 'Orders pagina', key: AccessKey.PAGE_ORDERS, right_type: UserRightType.ACCESS},
                {label: 'Aanvragen pagina', key: AccessKey.PAGE_INVOICE_REQUESTS, right_type: UserRightType.ACCESS},
                {label: 'Retouren pagina', key: AccessKey.PAGE_RETURNS, right_type: UserRightType.ACCESS},
                {label: 'Layout pagina', key: AccessKey.PAGE_INVOICE_LAYOUT, right_type: UserRightType.ACCESS},
            ]
        },
        {label: 'E-mails pagina', key: AccessKey.PAGE_EMAIL, right_type: UserRightType.ACCESS},
        {label: 'Notificaties pagina', key: AccessKey.PAGE_NOTIFICATIONS, right_type: UserRightType.ACCESS},
        {label: 'Sales Dashboard pagina', key: AccessKey.PAGE_SALES_DASHBOARD, right_type: UserRightType.ACCESS},
        {
            label: 'Review Inzicht pagina',
            key: AccessKey.PAGE_REVIEW_ANALYTICS,
            right_type: UserRightType.ACCESS,
            children: [
                {label: 'Producten tab', key: AccessKey.PAGE_REVIEWS_PRODUCTS, right_type: UserRightType.ACCESS},
                {label: 'Feed tab', key: AccessKey.PAGE_REVIEWS_FEED, right_type: UserRightType.ACCESS},
                {label: 'Instellingen tab', key: AccessKey.PAGE_REVIEWS_SETTINGS, right_type: UserRightType.ACCESS},
                {label: 'Exporteren tab', key: AccessKey.PAGE_REVIEWS_EXPORT, right_type: UserRightType.ACCESS},
            ]
        },
        {label: 'BTW Aangifte pagina', key: AccessKey.PAGE_VAT_DECLARATION, right_type: UserRightType.ACCESS},
        {label: 'Zoek Trends pagina', key: AccessKey.PAGE_SEARCH_TERMS, right_type: UserRightType.ACCESS},
        {
            label: 'Instellingen pagina', key: AccessKey.PAGE_SETTINGS, right_type: UserRightType.ACCESS, children: [
                {label: 'Gebruikersbeheer', key: AccessKey.PAGE_ACCOUNT_USERS, right_type: UserRightType.ACCESS},
                {label: 'Koppeling Bol tab', key: AccessKey.PAGE_SETTINGS_API, right_type: UserRightType.ACCESS},
                {label: 'Producten (BTW) tab', key: AccessKey.PAGE_SETTINGS_PRODUCTS, right_type: UserRightType.ACCESS},
                {
                    label: 'Factuurinstellingen tab',
                    key: AccessKey.PAGE_SETTINGS_INVOICE,
                    right_type: UserRightType.ACCESS
                },
                {label: 'E-mails tab', key: AccessKey.PAGE_SETTINGS_EMAILS, right_type: UserRightType.ACCESS},
                {label: 'Exporteren tab', key: AccessKey.PAGE_SETTINGS_EXPORT, right_type: UserRightType.ACCESS},
                {label: 'Abonnement tab', key: AccessKey.PAGE_SETTINGS_SUBSCRIPTION, right_type: UserRightType.ACCESS},
            ]
        },
    ],
    'Orders': [
        {
            label: 'Facturen', key: AccessKey.INVOICE, right_type: UserRightType.ACTION, children: [
                {label: 'Details inzien', key: AccessKey.INVOICE_DETAILS, right_type: UserRightType.ACTION},
                {label: 'Order statistieken inzien', key: AccessKey.INVOICE_STATS, right_type: UserRightType.ACTION},
                {
                    label: 'Klantgegevens wijzigen',
                    key: AccessKey.INVOICE_EDIT_CUSTOMER,
                    right_type: UserRightType.ACTION
                },
                {
                    label: 'Factuurregels wijzigen',
                    key: AccessKey.INVOICE_EDIT_ITEMS,
                    right_type: UserRightType.ACTION
                },
                {label: 'Downloaden/Voorbeeld', key: AccessKey.INVOICE_DOWNLOAD, right_type: UserRightType.ACTION},
                {label: 'Bulk downloaden', key: AccessKey.INVOICE_BULK_DOWNLOAD, right_type: UserRightType.ACTION},
                {label: 'Verzenden', key: AccessKey.INVOICE_SEND, right_type: UserRightType.ACTION},
                {label: 'Crediteren', key: AccessKey.INVOICE_CREDIT, right_type: UserRightType.ACTION},
                {label: 'BTW verleggen', key: AccessKey.INVOICE_REVERSE_CHARGE, right_type: UserRightType.ACTION},
                {label: 'Mails annuleren', key: AccessKey.INVOICE_CANCEL_EMAILS, right_type: UserRightType.ACTION},
                {
                    label: 'Factuurnummer instellen',
                    key: AccessKey.INVOICE_INVOICE_NUMBER,
                    right_type: UserRightType.ACTION
                },
            ]
        },
        {
            label: 'Aanvragen', key: AccessKey.INVOICE_REQUESTS, right_type: UserRightType.ACTION, children: [
                {label: 'Statistieken inzien', key: AccessKey.INVOICE_REQUEST_STATS, right_type: UserRightType.ACTION},
                {label: 'Facturen uploaden', key: AccessKey.INVOICE_REQUEST_UPLOAD, right_type: UserRightType.ACTION},
                {
                    label: 'Aanvragen archiveren',
                    key: AccessKey.INVOICE_REQUEST_ARCHIVE,
                    right_type: UserRightType.ACTION
                },
                {
                    label: 'Aanvragen instellingen',
                    key: AccessKey.INVOICE_REQUEST_SETTINGS,
                    right_type: UserRightType.ACCESS
                },
            ]
        },
        {
            label: 'Retouren', key: AccessKey.RETURN, right_type: UserRightType.ACTION, children: [
                {label: 'Details inzien', key: AccessKey.RETURN_DETAILS, right_type: UserRightType.ACTION},
                {label: 'Weergeven in Bol', key: AccessKey.RETURN_BOL, right_type: UserRightType.ACTION},
                {label: 'Creditfactuur verzenden', key: AccessKey.RETURN_SEND_CREDIT, right_type: UserRightType.ACTION},
                {
                    label: 'Creditfactuur aanmaken',
                    key: AccessKey.RETURN_CREATE_CREDIT,
                    right_type: UserRightType.ACTION
                },
            ]
        }
    ],
    'E-mails': [
        {label: 'E-mails wijzigen', key: AccessKey.EMAIL_EDIT, right_type: UserRightType.ACTION},
        {label: 'Statistieken inzien', key: AccessKey.EMAIL_STATS, right_type: UserRightType.ACTION},
        {label: 'Automatisch mailen wijzigen', key: AccessKey.EMAIL_EDIT_AUTOMATIC, right_type: UserRightType.ACTION},
        {label: 'Testmail verzenden', key: AccessKey.EMAIL_TEST_MAIl, right_type: UserRightType.ACTION},
        {label: 'E-mail kopiëren van ander account', key: AccessKey.EMAIL_COPY, right_type: UserRightType.ACTION},
    ],
    'Notificaties': [
        {label: 'Notificaties wijzigen', key: AccessKey.NOTIFICATIONS_EDIT, right_type: UserRightType.ACTION},
        {label: 'Notificaties verwijderen', key: AccessKey.NOTIFICATIONS_DELETE, right_type: UserRightType.ACTION},
    ],
    'Sales Dashboard': [
        {
            label: 'Productkosten instellen',
            key: AccessKey.SALES_DASHBOARD_COST_PRICES,
            right_type: UserRightType.ACTION
        },
        {label: 'Exporteren', key: AccessKey.SALES_DASHBOARD_EXPORT, right_type: UserRightType.ACTION},
    ],
    'Review Inzicht': [
        {label: 'Reviews inzien', key: AccessKey.RATING_VIEW, right_type: UserRightType.ACTION},
        {label: 'Instellingen wijzigen', key: AccessKey.RATING_SETTINGS, right_type: UserRightType.ACTION},
        {label: 'Export maken', key: AccessKey.RATING_CREATE_EXPORT, right_type: UserRightType.ACTION},
        {label: 'Export downloaden', key: AccessKey.RATING_DOWNLOAD_EXPORT, right_type: UserRightType.ACTION},
    ],
    'BTW Aangifte': [
        {label: 'Berekening exporteren', key: AccessKey.VAT_DECLARATION_EXPORT, right_type: UserRightType.ACTION},
        {
            label: 'Factuurspecificaties downloaden',
            key: AccessKey.VAT_DECLARATION_DOWNLOAD_SPEC,
            right_type: UserRightType.ACTION
        },
    ],
    'Instellingen': [
        {
            label: 'Algemeen', key: AccessKey.SETTINGS_COMMON, right_type: UserRightType.ACTION, children: [
                {
                    label: 'Bedrijfsgegevens wijzigen',
                    key: AccessKey.SETTING_EDIT_COMPANY,
                    right_type: UserRightType.ACTION
                },
                {label: 'Account verwijderen', key: AccessKey.SETTINGS_DELETE_COMPANY, right_type: UserRightType.ACTION}
            ]
        },
        {
            label: 'Gebruikers', key: AccessKey.SETTINGS_ACCOUNT_USERS, right_type: UserRightType.ACTION, children: [
                {label: 'Gebruikers toevoegen', key: AccessKey.ACCOUNT_USERS_INVITE, right_type: UserRightType.ACTION},
                {label: 'Gebruikers wijzigen', key: AccessKey.ACCOUNT_USERS_EDIT, right_type: UserRightType.ACTION},
            ]
        },
        {
            label: 'Koppeling Bol', key: AccessKey.API_SETTINGS, right_type: UserRightType.ACTION, children: [
                {
                    label: 'Koppeling verbreken',
                    key: AccessKey.API_SETTINGS_DISCONNECT,
                    right_type: UserRightType.ACTION
                },
                {label: 'Koppeling instellen', key: AccessKey.API_SETTINGS_SETUP, right_type: UserRightType.ACTION}
            ]
        },
        {
            label: 'Producten (BTW)', key: AccessKey.PRODUCT_SETTINGS, right_type: UserRightType.ACTION, children: [
                {
                    label: 'Productnaam wijzigen',
                    key: AccessKey.PRODUCT_SETTINGS_EDIT_TITLE,
                    right_type: UserRightType.ACTION
                },
                {label: 'BTW% wijzigen', key: AccessKey.PRODUCT_SETTINGS_EDIT_VAT, right_type: UserRightType.ACTION},
                {label: 'Sync met bol', key: AccessKey.PRODUCT_SETTINGS_SYNC, right_type: UserRightType.ACTION},
                {
                    label: 'Bulk percentages uploaden',
                    key: AccessKey.PRODUCT_SETTINGS_UPLOAD,
                    right_type: UserRightType.ACTION
                },
            ]
        },
        {
            label: 'Factuurinstellingen', key: AccessKey.INVOICE_SETTINGS, right_type: UserRightType.ACTION, children: [
                {
                    label: 'Instellingen wijzigen',
                    key: AccessKey.INVOICE_SETTINGS_EDIT,
                    right_type: UserRightType.ACTION
                },
            ]
        },
        {
            label: 'E-mails', key: AccessKey.EMAIL_SETTINGS, right_type: UserRightType.ACTION, children: [
                {label: 'Instellingen wijzigen', key: AccessKey.EMAIL_SETTINGS_EDIT, right_type: UserRightType.ACTION},
            ]
        },
        {
            label: 'Exporteren', key: AccessKey.EXPORT_SETTINGS, right_type: UserRightType.ACTION, children: [
                {label: 'Facturen exporteren', key: AccessKey.EXPORT_SETTINGS_EXPORT, right_type: UserRightType.ACTION},
                {
                    label: 'Exports downloaden',
                    key: AccessKey.EXPORT_SETTINGS_DOWNLOAD,
                    right_type: UserRightType.ACTION
                },
            ]
        }
    ],
};